import { useState } from "react";
import { Button, Row, Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Copy01Icon,
  Edit02Icon,
  FilterIcon,
  Location03Icon,
  Search01Icon,
} from "@hugeicons/core-free-icons/index";
import { useTranslation } from "react-i18next";
import { ArrivalCard } from "../Reception/ArrivalCard";
import { useAllHousingInspectionQuery } from "@/services/houseInspections";
import { useTableFilter } from "@/utils/hooks/useFilter";
import { Badge, Checkbox, Collapse, Divider, Flex, Pagination } from "antd";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DATE_TIME_FORMAT, EXAMINATION_TYPES, TIME_FORMAT, TIME_FORMAT_12_HOURS, permissions } from "@/utils/constants";
import { Link, useLocation, useNavigate } from "react-router";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { AntInput } from "@/CommonElements/AntInput";
import dayjs from "dayjs";
import { useHousingInspectionContext } from "./HousingInspectionContext/housingContext";
import { SelectStickyAction } from "@/Component/SelectStickyAction";
import { CountModal } from "@/Component/CountModal";
import { Col } from "reactstrap";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { GenericFilterModal } from "@/CommonElements/TableC";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import BtnAsLink from "@/Component/BtnAsLink";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { useSystem } from "@/utils/hooks/useLocation";
import { Button as AntButton } from "antd";
import { toast } from "react-toastify";
import { Can } from "@/CommonElements/PermissionGate";

export const HousingInspectionList = () => {
  const navigate = useNavigate();
  const { confirm, ModalComponent } = useConfirm();
  // ==================
  // Hooks
  // ==================
  const location = useLocation();
  const cityId = location.pathname.includes("madina") ? 4 : 3;

  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();

  const { currentSystem,isMakkah } = useSystem();

  const { filters, handleFilterApply, handleFilterReset, filterFields, hasActiveFilters } = useTableFilter([
    {
      name: "date",
      label: t("CONTRACT_START_DATE_FROM"),
      type: "date",
      size: "large",
      style: {
        height: "50px",
      },
    },
    {
      name: "date",
      label: t("CONTRACT_START_DATE_TO"),
      type: "date",
      size: "large",
      style: {
        height: "50px",
      },
    },
    {
      name: "city_id",
      label: t("BUILDING_CITY"),
      type: "select",
      optionKey: "cities",
      filter: (c) => c.id === 4 || c.id === 3,
      options: (city) => {
        return {
          label: <LabelValue.Value value={getLocalizedText(city.name_ar, city.name_en)} />,
          value: city.id,
        };
      },
    },
    {
      name: "country_id",
      label: t("HOUSING_INSPECTION_CLIENT_NAME"),
      type: "select",
      optionKey: "nationalities",
      options: (s) => {
        return {
          label: <SelectCountry.Display value={s.id} />,
          value: s.id,
        };
      },
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",

      options: (s) => {
        return {
          label: <LabelValue.Value value={s.name} />,
          value: s.id,
        };
      },
    },
  ]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);
  const [showClearSelectionConfirm, setShowClearSelectionConfirm] = useState(false);
  const {
    searchParams,
    inspectionState: { selectedInspections, type },
    addSelectedInspection,
    removeSelectedInspection,
    clearSelectedInspections,
  } = useHousingInspectionContext();

  let examination_status = searchParams.get("tab") || "";

  examination_status = examination_status === "all" ? "" : examination_status;

  const [currentPage, setCurrentPage] = useState(1);

  const { data: InspectionRes, isLoading } = useAllHousingInspectionQuery({
    ...filters,
    page: currentPage,
    per_page: 10,
    examination_status,
    search: debouncedValue,
    city_id: cityId,
  });

  const InspectionData = InspectionRes?.data?.data ?? [];
  const meta = InspectionRes?.data?.meta;

  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value);
    toast.success(t("COPIED_TO_CLIPBOARD"), { position: "top-right" });
  };

  const labelValues = [
    {
      key: "permit_no",
      label: t("PERMIT_NUMBER"),
      render: (value, rowData) => <LabelValue.Value value={rowData?.house?.permit_no || "-"} />,
    },
    {
      key: "houseCommercialNameAr",
      label: t("HOUSING_INSPECTION_BUILDING_NAME"),
      render: (value, rowData) => <LabelValue.Value value={rowData?.house?.name || getLocalizedText(rowData?.house?.houseCommercialNameAr, rowData?.house?.houseCommercialNameEn) || "-"} />,
    },
    {
      key: "address",
      label: t("HOUSING_INSPECTION_BUILDING_ADDRESS"),
      fullWidth: true,
      render: (value, rowData) => (
        <Flex align="center" justify="space-between" gap={2}>
          <LabelValue.Value value={rowData.house.address || "-"} />
          <Flex align="center" gap={6}>
            <AntButton
              title={t("PREVIEW")}
              target="_blank"
              size="middle"
              type="default"
              disabled={!rowData.house.Location}
              href={rowData.house.Location}
              icon={<HugeiconsIcon size={20} icon={Location03Icon} />}
            />
            <AntButton
              title={t("COPY")}
              type="default"
              size="middle"
              disabled={!rowData.house.Location}
              onClick={copyToClipboard.bind(null, rowData.house.Location)}
              icon={<HugeiconsIcon size={20} icon={Copy01Icon} />}
            />
          </Flex>
        </Flex>
      ),
    },
    {
      key: "OwnerName",
      label: t("HOUSING_INSPECTION_DELEGATION_NAME"),
      render: (value, rowData) => <LabelValue.Value value={rowData.house.OwnerName || "-"} />,
    },
    {
      key: "REP_MOBILE_NO",
      label: t("HOUSING_INSPECTION_MOBILE_NUMBER"),
      render: (value, rowData) => <LabelValue.Value value={rowData.house.REP_MOBILE_NO || "-"} />,
    },
    {
      key: "examination_date",
      label: t("HOUSING_INSPECTION_INSPECTION_TIME"),
      render: (value, rowData) => (
        <LabelValue.Value
          value={
            !!rowData?.order?.examination_date
              ? getLocalizedDate(rowData?.order?.examination_date, DATE_TIME_FORMAT)
              : "-"
          }
        />
      ),
    },
    {
      key: "examination_count",
      label: t("HOUSING_INSPECTION_INSPECTION_COUNT"),
      render: (value, rowData) => (
        <LabelValue.Value value={!!rowData?.order?.order_number ? rowData.order.order_number : "-"} />
      ),
    },
    {
      key: "contracts",
      label: t("CONTRACTS"),
      render: (value) => {
        const contract = value[0];
        const count = value.length;

        return (
          <CountModal
            count={count}
            modal={{
              props: {
                size: "lg",
                centered: true,
                scrollable: true,
              },
              header: {
                children: t("CONTRACTS"),
              },
              body: {
                children: (
                  <ul style={{ paddingInlineStart: "15px" }}>
                    {value.map((c) => (
                      <li key={c.id}>
                        <Row>
                          <div className="mb-2">
                            {[
                              c.id,
                              getLocalizedText(
                                c.missionNameAr || c.company_name_ar,
                                c.missionNameEn || c.company_name_en,
                              ),
                            ]
                              .filter(Boolean)
                              .join(" - ")}
                          </div>

                          <COL >
                            <LabelValue
                              label={t("CONTRACT_START_DATE")}
                              value={
                                !!c?.contractStartDate ? getLocalizedDate(c?.contractStartDate, DATE_TIME_FORMAT) : "-"
                              }
                              direction="vertical"
                            />
                          </COL>
                          <COL >
                            <LabelValue
                              label={t("CONTRACT_END_DATE")}
                              value={
                                !!c?.contractEndDate ? getLocalizedDate(c?.contractEndDate, DATE_TIME_FORMAT) : "-"
                              }
                              direction="vertical"
                            />
                          </COL>
                          <COL >
                            <LabelValue
                              label={t("SERVICE_CENTER")}
                              value={
                                getLocalizedText(c?.service_center?.name_ar, c?.serviceCenter?.name_en) || "-"
                              }
                              direction="vertical"
                            />
                          </COL>
                        </Row>
                        <Divider />
                      </li>
                    ))}
                  </ul>
                ),
              },
            }}
          >
            {[
              contract.id,
              getLocalizedText(
                contract.missionNameAr || contract.company_name_ar,
                contract.missionNameEn || contract.company_name_en,
              ),
            ]
              .filter(Boolean)
              .join(" - ")}
          </CountModal>
        );
      },
    },
  ];

  const getStatusConfig = (status) => {
    const statusMap = {
      completed: {
        color: "success",
        text: getLocalizedText(status?.label_ar, status?.label_en),
      },
      not_scheduled: {
        color: "danger",
        text: getLocalizedText(status?.label_ar, status?.label_en),
      },
      in_progress: {
        color: "brand",
        text: getLocalizedText(status?.label_ar, status?.label_en),
      },

      qualified: {
        color: "success",
        text: getLocalizedText(status?.label_ar, status?.label_en),
      },
      not_qualified: {
        color: "danger",
        text: getLocalizedText(status?.label_ar, status?.label_en),
      },
    };

    return (
      statusMap[status?.value] || {
        color: "brand",
        text: t("لم يتم انشاء امر فحص"),
      }
    );
  };

  // Create action buttons for each arrival
  const createActions = (item) => {
    const mapActions = {
      not_scheduled: (
        <Link
          to={`/app/${currentSystem}/housing-inspection-orders/${item?.order?.order_number}/edit`}
          className="btn btn-outline btn-brand flex-fill"
        >
          <Stack direction="horizontal" gap={2}>
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            <span>{t("EDIT_HOUSING_INSPECTION")}</span>
          </Stack>
        </Link>
      ),
      // completed: (
      //   <Button className="btn-outline btn-brand flex-fill" onClick={() => { }}>
      //     <Stack direction="horizontal" gap={2}>
      //       <HugeiconsIcon icon={ArrowReloadVerticalIcon} />
      //       <span>{t("RE_INSPECT_HOUSING_INSPECTION")}</span>
      //     </Stack>
      //   </Button>
      // ),
      // in_progress: (
      //   <Button
      //     disabled
      //     className="btn-outline btn-brand flex-fill"
      //     onClick={() => { }}
      //   >
      //     <Stack direction="horizontal" gap={2}>
      //       <HugeiconsIcon icon={ArrowReloadVerticalIcon} />
      //       <span>{t("RE_INSPECT_HOUSING_INSPECTION")}</span>
      //     </Stack>
      //   </Button>
      // ),
    };

    return mapActions[item?.order?.examination_status?.value ?? "create"];
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const skeletonItems = Array.from({ length: 3 }, (_, index) => ({
    id: index,
  }));
  const handleConfirmClearSelection = () => {
    clearSelectedInspections();
  };
  const handleCheckboxChange = (item, checked) => {
    if (checked) {
      addSelectedInspection(item);
    } else {
      removeSelectedInspection(item);
    }
  };
  return (
    <div>
      <Row className="flex-wrap">
        <Stack className="mb-4" gap={4} direction="horizontal">
          <AntInput
            prefix={<HugeiconsIcon icon={Search01Icon} />}
            placeholder={t("SEARCH_BUILDING_NAME_OR_PERMISSION_NUMBER")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value || "")}
          />
          <Flex gap={8}>
            <Badge count={Object.values(filters || {})?.filter?.(Boolean)?.length}>
              <BtnAsLink
                className="btn-secondary"
                icon={<HugeiconsIcon icon={FilterIcon} />}
                onClick={() => setOpenFilterModal(true)}
              >
                {t("FILTER")}
              </BtnAsLink>
            </Badge>
          </Flex>
        </Stack>
        {skeletonItems.map((item) => (
          <ArrivalCard loading={isLoading} arrival={null} key={item.id} />
        ))}
        {!InspectionData?.length && hasActiveFilters && !isLoading && (
          <div className="text-center py-5">
            <HugeiconsIcon size={40} icon={Search01Icon} />
            <p>{t("NO_HOUSING_INSPECTION_ORDERS_FOUND")}</p>
          </div>
        )}
        {InspectionData?.map((item) => {

          // show when any of the following:
          // 1- no order
          // 2- has order + order completed + status = not qualified  

          const cardStatus =
            item?.house?.examination_result?.status || item?.order?.examination_status;


          const isNotQualified = cardStatus?.value === "not_qualified";
          const isCompleted = item?.order?.examination_status?.value === "completed";
          
          const shouldShowCheckbox = !item?.order  ||  (item?.order && isCompleted && isNotQualified);
            // (!item?.order || item?.order?.examination_status?.value === "completed") &&
            // cardStatus?.value === "not_qualified";

          return (
            <ArrivalCard
              key={item.house.id}
              title={
                <Flex gap={8}>
                  {shouldShowCheckbox && (
                  <Can
                    permissions={[
                      isMakkah
                        ? permissions.create_house_inspection_order
                        : permissions.create_house_inspection_order_for_madina
                    ]}
                  >
                    <Checkbox
                      checked={selectedInspections?.some((inspection) => inspection.house.id === item.house.id)}
                      className="square-checkbox"
                      onChange={(val) => {
                        if (type === EXAMINATION_TYPES.track) {
                          confirm({
                            title: t("CONFIRM"),
                            content: t("CONFIRM_CLEAR_TRACKED_ITEMS"),
                            okText: t("CONFIRM"),
                            cancelText: t("CANCEL"),
                            onOk: () => {
                              clearSelectedInspections();
                              handleCheckboxChange(item, val.target.checked);
                            },
                            onCancel: () => {},
                          });

                          return;
                        }
                        handleCheckboxChange(item, val.target.checked);
                      }}
                    />
                  </Can>
                  )}
                  <span>{t("HOUSING_INSPECTION_NUMBER")}: </span>
                </Flex>
              }
              titleValue={item.order?.order_code || "-"}
              arrival={item}
              statusConfig={getStatusConfig(cardStatus)}
              // actions={createActions(item)}
              labelValues={labelValues}
              extra={
                item.order?.id && (
                <Collapse
                  size="small"
                  items={[
                    {
                      key: "1",
                      label: t("HOUSING_INSPECTION_ORDERS"),
                      children: (
                        <ul style={{ listStyle: "disc" }}>
                          <li>
                            <span>
                              {t("HOUSING_INSPECTION_ORDERS")}: {item.order?.order_code || "-"}
                            </span>
                          </li>
                          <li>
                            <span>
                              {t("ORDER_CODE")}: {item.order?.period_code?.code || "-"}
                            </span>
                          </li>
                          <li>
                            <span>
                              {t("EXAMINATION_STATUS")}:{" "}
                              {getLocalizedText(
                                item.order?.examination_status?.label_ar,
                                item.order?.examination_status?.label_en,
                              )}
                            </span>
                          </li>
                          <li>
                            <span>
                              الفترة:{" "}
                              <Stack direction="horizontal" gap={2} className="flex-wrap">
                                <span>
                                  {item.order?.examination_period?.id} -{" "}
                                  {getLocalizedText(
                                    item.order?.examination_period?.name_ar,
                                    item.order?.examination_period?.name_en,
                                  )}
                                </span>
                                <span dir="ltr" className="text-uppercase">
                                  {dayjs(item.order?.examination_period?.time_to, TIME_FORMAT).format(
                                    TIME_FORMAT_12_HOURS,
                                  )}
                                </span>
                                -
                                <span dir="ltr" className="text-uppercase">
                                  {dayjs(item.order?.examination_period?.time_from, TIME_FORMAT).format(
                                    TIME_FORMAT_12_HOURS,
                                  )}
                                </span>
                              </Stack>
                            </span>
                          </li>
                        </ul>
                      ),
                    },
                  ]}
                />
                )
              }
            />
          );
        })}
      </Row>
      <ModalComponent />
      <ActionsFooter>
        <AntPagination
          hideOnSinglePage
          pageSize={10}
          current={currentPage}
          onChange={handlePageChange}
          total={meta?.total ?? 0}
          showSizeChanger={false}
        />
      </ActionsFooter>
      <Can
        permissions={[
          isMakkah
            ? permissions.create_house_inspection_order
            : permissions.create_house_inspection_order_for_madina
        ]}
      >
        {type !== "track" && (
          <SelectStickyAction
            modalProps={{
              showClearSelectionConfirm,
              setShowClearSelectionConfirm,
              handleConfirmClearSelection: handleConfirmClearSelection,
            }}
            okButtonText={t("CREATE_HOUSING_INSPECTION")}
            selectedCount={selectedInspections.length}
            handleClearSelectionClick={() => {
              setShowClearSelectionConfirm(true);
            }}
            handleSubmitSelected={() => {
              navigate(`/app/${currentSystem}/housing-inspection-orders/create`, {
                state: {
                  selectedInspections,
                },
              });
            }}
          />
        )}
      </Can>

      <GenericFilterModal
        isOpen={openFilterModal}
        toggle={() => setOpenFilterModal(false)}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filters={filters}
        filterFields={filterFields}
      />
    </div>
  );
};

const COL = ({ children }) => (
  <Col xs={12} sm={12} md={4} lg={4} xl={4}>
    {children}
  </Col>
);
